import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { CheckCircle2, Download, FileText, ShieldCheck, XCircle } from 'lucide-react';
import { generateAdmissionReceipt } from '../utils/receiptPdf';
import { getAdmissionTypeLabel } from '../utils/admissionType';

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return `${date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
  })} at ${date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata'
  })}`;
};

const ReceiptRow = ({ label, value, accent }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4 py-3 border-b border-slate-100 last:border-b-0">
    <dt className="text-xs font-extrabold uppercase tracking-wider text-slate-400">{label}</dt>
    <dd className={`text-sm font-bold ${accent || 'text-slate-900'}`}>{value || 'N/A'}</dd>
  </div>
);

export default function DigitalReceiptPage() {
  const { applicationId } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReceipt = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`/api/applications/public-receipt/${applicationId}`);
        setReceipt(res.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Receipt not found');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();
  }, [applicationId]);

  const status = useMemo(() => {
    if (receipt?.status === 'cancelled') {
      return {
        label: 'Pre Registration Cancelled',
        icon: XCircle,
        className: 'bg-rose-50 text-rose-700 border-rose-100'
      };
    }

    return {
      label: 'Pre Registration Confirmed',
      icon: CheckCircle2,
      className: 'bg-emerald-50 text-emerald-700 border-emerald-100'
    };
  }, [receipt?.status]);

  const handleDownload = async () => {
    if (!receipt) return;
    const toastId = toast.loading('Generating receipt...');
    try {
      await generateAdmissionReceipt({
        application: receipt,
        applicationId: receipt.applicationId,
        paymentId: receipt.paymentId,
        studentName: receipt.studentName
      });
      toast.success('Receipt downloaded!', { id: toastId });
    } catch (err) {
      console.error('Digital receipt PDF error:', err);
      toast.error('Receipt generation failed.', { id: toastId });
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg)' }}>
        <div className="w-10 h-10 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin" />
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg)' }}>
        <div className="max-w-md w-full rounded-2xl border bg-white p-8 text-center shadow-sm" style={{ borderColor: 'var(--card-border)' }}>
          <FileText size={42} className="mx-auto mb-4 text-slate-300" />
          <h1 className="text-xl font-extrabold text-slate-900">Receipt unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
        </div>
      </main>
    );
  }

  const StatusIcon = status.icon;

  return (
    <main className="min-h-screen px-4 py-10 md:py-14" style={{ background: 'var(--bg)' }}>
      <section className="max-w-3xl mx-auto bg-white border shadow-sm overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
        <div className="bg-black text-white px-6 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold tracking-[0.22em] text-amber-300 uppercase">SeatifyAI</p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold">Receipt</h1>
          </div>
          <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-extrabold ${status.className}`}>
            <StatusIcon size={16} />
            {status.label}
          </div>
        </div>

        <div className="px-6 sm:px-8 py-6">
          {/* <div className="flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 mb-6">
            <ShieldCheck size={20} className="text-emerald-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-semibold text-emerald-800">
              This public receipt is generated from the application record for verification.
            </p>
          </div> */}

          <dl>
            <ReceiptRow label="Application ID" value={receipt.applicationId} accent="text-indigo-700 font-mono" />
            <ReceiptRow label="Student Name" value={receipt.studentName} />
            <ReceiptRow label="Institution" value={receipt.collegeName} />
            <ReceiptRow label="Program" value={receipt.programName} />
            <ReceiptRow label="Course" value={receipt.courseName} />
            <ReceiptRow label="Admission Type" value={getAdmissionTypeLabel(receipt.admissionType)} />
            <ReceiptRow label="Academic Year" value={receipt.academicYear || 'N/A'} />
            <ReceiptRow label="Amount Paid" value={`INR ${(receipt.fee || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            <ReceiptRow label="Payment ID" value={receipt.paymentId} />
            <ReceiptRow label="Issued On" value={formatDateTime(receipt.createdAt)} />
            {receipt.status === 'cancelled' && (
              <ReceiptRow label="Cancelled On" value={formatDateTime(receipt.cancelledAt)} accent="text-rose-700" />
            )}
          </dl>

          <div className="mt-7 flex justify-end">
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 bg-black  rounded-xl px-5 py-3 text-sm font-extrabold text-white shadow-lg shadow-indigo-100 transition-all hover:bg-indigo-700 active:scale-95"
            >
              <Download size={18} />
              Download PDF
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
